import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const DISCIPLINES = [
  "Powerlifting",
  "CrossFit",
  "Yoga",
  "Running",
  "HIIT",
  "Bodybuilding",
  "Calisthenics",
  "Olympic Lifting",
  "Boxing",
  "Pilates",
  "Spin",
  "Hiking",
];

const SAMPLE_PHOTOS = [
  "https://images.unsplash.com/photo-1605296867424-35fc25c9212a?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1599058917212-d750089bc07e?auto=format&fit=crop&w=900&q=80",
  "https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&w=900&q=80",
];

const Onboarding = () => {
  const { user, updateProfile } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: user?.name || "",
    age: user?.age || "",
    gender: user?.gender || "",
    photo_url: user?.photo_url || SAMPLE_PHOTOS[0],
    bio: user?.bio || "",
    gym: user?.gym || "",
    location_city: user?.location_city || "",
    training_disciplines: user?.training_disciplines || [],
    goals: user?.goals || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDiscipline = (d) =>
    setForm((f) => ({
      ...f,
      training_disciplines: f.training_disciplines.includes(d)
        ? f.training_disciplines.filter((x) => x !== d)
        : [...f.training_disciplines, d],
    }));

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    if (form.training_disciplines.length === 0) {
      toast.error("Pick at least one training discipline.");
      setStep(2);
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        name: form.name,
        age: form.age ? Number(form.age) : null,
        gender: form.gender,
        photo_url: form.photo_url,
        photos: [form.photo_url],
        bio: form.bio,
        gym: form.gym,
        location_city: form.location_city,
        training_disciplines: form.training_disciplines,
        goals: form.goals,
        onboarded: true,
      });
      toast.success("Profile ready. Let's find your match.");
      nav("/app/discover");
    } catch (err) {
      toast.error("Could not save your profile. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const stepTitles = ["The Basics", "Your Vibe", "How You Train", "Your Goals"];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-10">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-10">
          {stepTitles.map((title, i) => (
            <div
              key={title}
              className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                i <= step ? "bg-[#C6FF00]" : "bg-white/10"
              }`}
            />
          ))}
        </div>

        <p className="text-xs uppercase tracking-[0.3em] text-[#C6FF00] font-semibold mb-3">
          Step {step + 1} of 4
        </p>
        <h1 className="font-heading text-5xl md:text-6xl leading-[0.95] mb-8" data-testid="onboarding-step-title">
          {stepTitles[step]}
        </h1>

        {/* Step content */}
        {step === 0 && (
          <div className="flex flex-col gap-4">
            <Field label="Name">
              <input
                data-testid="ob-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="onboard-input"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Age">
                <input
                  data-testid="ob-age"
                  type="number"
                  value={form.age}
                  onChange={(e) => update("age", e.target.value)}
                  className="onboard-input"
                  min={18}
                  max={99}
                />
              </Field>
              <Field label="Gender">
                <select
                  data-testid="ob-gender"
                  value={form.gender}
                  onChange={(e) => update("gender", e.target.value)}
                  className="onboard-input"
                >
                  <option value="">Choose…</option>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
            <Field label="City">
              <input
                data-testid="ob-city"
                value={form.location_city}
                onChange={(e) => update("location_city", e.target.value)}
                placeholder="Atlanta, GA"
                className="onboard-input"
              />
            </Field>
          </div>
        )}

        {step === 1 && (
          <div>
            <p className="text-sm text-neutral-400 mb-4">Pick your profile photo.</p>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {SAMPLE_PHOTOS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => update("photo_url", p)}
                  data-testid={`ob-photo-${SAMPLE_PHOTOS.indexOf(p)}`}
                  className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                    form.photo_url === p ? "border-[#C6FF00] scale-[1.02]" : "border-white/10"
                  }`}
                >
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  {form.photo_url === p && (
                    <div className="absolute inset-0 bg-[#C6FF00]/15 ring-2 ring-[#C6FF00]" />
                  )}
                </button>
              ))}
            </div>
            <Field label="Photo URL (optional, paste any image link)">
              <input
                data-testid="ob-photo-url"
                value={form.photo_url}
                onChange={(e) => update("photo_url", e.target.value)}
                className="onboard-input"
              />
            </Field>
            <Field label="Short bio">
              <textarea
                data-testid="ob-bio"
                rows={3}
                value={form.bio}
                onChange={(e) => update("bio", e.target.value)}
                placeholder="What do you bring to the gym floor?"
                className="onboard-input resize-none"
              />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-sm text-neutral-400 mb-6">
              Tap the disciplines you live by. We surface partners around these.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              {DISCIPLINES.map((d) => {
                const active = form.training_disciplines.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    data-testid={`ob-disc-${d.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => toggleDiscipline(d)}
                    className={`px-5 py-3 rounded-full text-sm font-semibold uppercase tracking-[0.15em] transition-all border ${
                      active
                        ? "bg-[#C6FF00] text-[#0A0A0A] border-[#C6FF00] shadow-[0_0_20px_rgba(198,255,0,0.35)]"
                        : "bg-transparent text-neutral-200 border-white/15 hover:border-[#C6FF00]"
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <Field label="Home gym (optional)">
              <input
                data-testid="ob-gym"
                value={form.gym}
                onChange={(e) => update("gym", e.target.value)}
                className="onboard-input"
                placeholder="Iron Republic"
              />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div>
            <Field label="What are you chasing?">
              <textarea
                data-testid="ob-goals"
                rows={4}
                value={form.goals}
                onChange={(e) => update("goals", e.target.value)}
                placeholder="e.g. 4-plate squat. Sub-3 marathon. Find a partner who shows up."
                className="onboard-input resize-none"
              />
            </Field>
          </div>
        )}

        {/* Nav */}
        <div className="mt-10 flex items-center justify-between">
          <button
            data-testid="ob-back"
            onClick={back}
            disabled={step === 0}
            className="text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-[#C6FF00] font-semibold disabled:opacity-30 py-2"
          >
            ← Back
          </button>
          {step < 3 ? (
            <button
              data-testid="ob-next"
              onClick={next}
              className="inline-flex items-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-7 py-4 rounded-md transition-all hover:scale-105 shadow-[0_0_20px_rgba(198,255,0,0.25)]"
            >
              Next <ArrowRight size={16} />
            </button>
          ) : (
            <button
              data-testid="ob-finish"
              onClick={finish}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-7 py-4 rounded-md transition-all hover:scale-105 disabled:opacity-60 shadow-[0_0_20px_rgba(198,255,0,0.25)]"
            >
              {saving ? "Saving…" : "Start Matching"} <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>

      <style>{`
        .onboard-input {
          width: 100%;
          background: #111111;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.5rem;
          padding: 0.85rem 1rem;
          color: #fff;
          font-family: inherit;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.2s;
        }
        .onboard-input:focus {
          border-color: #C6FF00;
          box-shadow: 0 0 0 1px #C6FF00;
        }
        .onboard-input::placeholder { color: #525252; }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-2">
      {label}
    </label>
    {children}
  </div>
);

export default Onboarding;
